import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "../../../../../Translation";
import { ArrowDownIcon, ArrowsPointingOutIcon, ChevronDownIcon, Cog8ToothIcon, DocumentIcon, ExclamationCircleIcon, MinusIcon, PlusIcon, TableCellsIcon } from "@heroicons/react/24/outline";
import { webhook } from "../../../../../Resources/Images";
import { TextInput } from "../../../../Inputs/TextInput";
import { TextArea } from "../../../../Inputs/TextArea";
import { Button } from "../../../../Buttons";
import Tabs, { useTabs } from "../../../../Tabs";
import { useAPI } from "../../../../../API";
import Select from "../../../../Inputs/Select";
import { bgColor, hoverBgColor } from "../../../../../Resources/CSS/styling";
import { escapeForBackend } from "../../../../../Utils";

const ConfigurationTab = ({ tmpStep, setTmpStep, save, error, isCreate, userApp, appWebhookStep }) => {

    const { translate } = useTranslation();
    const api = useAPI();

    const [audienceId, setAudienceId] = useState(tmpStep.data.audience_id || '');

    const schemaOption = [
        { id: 1,  name: translate('automatisation.meta_audience_schema.email'),               value: 'EMAIL',              hash: true  },
        { id: 2,  name: translate('automatisation.meta_audience_schema.phone'),               value: 'PHONE',              hash: true  },
        { id: 3,  name: translate('automatisation.meta_audience_schema.external_id'),         value: 'EXTERN_ID',          hash: true  },
        { id: 4,  name: translate('automatisation.meta_audience_schema.mobile_advertiser_id'), value: 'MOBILE_ADVERTISER_ID', hash: true  },
        { id: 5,  name: translate('automatisation.meta_audience_schema.lookalike_value'),    value: 'LOOKALIKE_VALUE',    hash: false }, // numeric, no hash
        { id: 6,  name: translate('automatisation.meta_audience_schema.gender'),              value: 'GEN',                hash: true  },
        { id: 8,  name: translate('automatisation.meta_audience_schema.city'),                value: 'CT',                 hash: true  },
        { id: 9,  name: translate('automatisation.meta_audience_schema.state'),               value: 'ST',                 hash: true  },
        { id: 10, name: translate('automatisation.meta_audience_schema.zip'),                 value: 'ZIP',                hash: true  },
        { id: 11, name: translate('automatisation.meta_audience_schema.country'),             value: 'COUNTRY',            hash: true  },
        { id: 12, name: translate('automatisation.meta_audience_schema.last_name'),           value: 'LN',                 hash: true  },
        { id: 13, name: translate('automatisation.meta_audience_schema.first_name'),          value: 'FN',                 hash: true  },
    ];
    
    const [schema, setSchema] = useState(tmpStep.data.schema || [schemaOption[0]]);

    const onDataChange = (schema, audienceId) => {

        const body = {
            payload: {
                schema: schema.map(schema => schema.value),
                data: [schema.map(schema => schema.hash ? "func[sha256(" + schema.textValue + ")]" : schema.textValue)]
            }
        };

        const APICallStepData = {
            method: 'POST',
            url: 'https://graph.facebook.com/v20.0/' + audienceId + '/users',
            body: body,
            headers: {
                'Authorization': 'Bearer ' + userApp.data.access_token
            },
            schema: schema,
            audience_id: audienceId
        }

        setTmpStep({ ...tmpStep, app_webhook_unique_identifier: appWebhookStep.webhook_unique_identifier, user_app_id: userApp.id, data: APICallStepData });
    }

    const addSchema = () => {
        const newSchema = [...schema, schemaOption[0]];
        setSchema(newSchema);
        onDataChange(newSchema, audienceId);
    }

   
    const updateSchema = (schemaSelected, index) => {
        const newSchema = [...schema];
        newSchema[index] = {...newSchema[index], ...schemaSelected};
        setSchema(newSchema);
        onDataChange(newSchema, audienceId);
    }

    const updateSchemaValue = (value, index) => {
        const newSchema = [...schema];
        newSchema[index].textValue = value;
        setSchema(newSchema);
        onDataChange(newSchema, audienceId);
    }

    const removeSchema = (index) => {
        const newSchema = [...schema];
        newSchema.splice(index, 1);
        setSchema(newSchema);
        onDataChange(newSchema, audienceId);
    }

    const updateAudienceId = (value) => {
        setAudienceId(value);
        onDataChange(schema, value);
    }


    return (
        <div className="flex flex-col mt-4">

            {error && <p className="text-red-600 flex flex-row items-center bg-red-50 p-4"><ExclamationCircleIcon className="w-6 h-6 mr-2" />{error}</p>}
            <div className="flex flex-col mt-4">

                <p className="text-xl text-gray-600 font-bold">{translate('automatisation.audience_id')}</p>
                <TextInput value={audienceId} onChange={(e) => updateAudienceId(e.target.value)} />

                <div className="flex flex-row items-center justify-between mt-4">
                    <p className="text-xl text-gray-600 font-bold">{translate('automatisation.schemas')}</p>
                    <PlusIcon onClick={addSchema} className="w-6 h-6 text-gray-600 cursor-pointer hover:text-gray-300" />
                </div>
                <div className="flex flex-col flex-wrap">
                    <div className="flex flex-col flex-wrap border border-gray-400 rounded-md p-2 mt-4">
                        <div className="flex flex-row items-center justify-between">
                            <p className="text-md text-gray-600 font-medium">{translate('automatisation.schemas_identifier')}</p>
                            {/* <MinusIcon className="w-6 h-6 text-red-600 cursor-pointer hover:text-red-400" /> */}
                        </div>
                        <Select options={schemaOption} selected={schema[0]} setSelected={(e) => updateSchema(e, 0)} />
                        <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.schemas_value')}</p>
                        <TextInput value={schema[0].textValue} onChange={(e) => updateSchemaValue(e.target.value, 0)} />

                    </div>
                    {schema.map((schemaIteration, index) => index > 0 && (
                        <div className="flex flex-col flex-wrap border border-gray-400 rounded-md p-2 mt-4">
                            <div className="flex flex-row items-center justify-between">
                                <p className="text-md text-gray-600 font-medium">{translate('automatisation.schemas_identifier')}</p>
                                <MinusIcon onClick={() => removeSchema(index)} className="w-6 h-6 text-red-600 cursor-pointer hover:text-red-400" />
                            </div>
                            <Select options={schemaOption} selected={schemaIteration} setSelected={(e) => updateSchema(e, index)} />
                            <p className="text-md text-gray-600 font-medium mt-2">{translate('automatisation.schemas_value')}</p>
                            <TextInput value={schemaIteration.textValue} onChange={(e) => updateSchemaValue(e.target.value, index)} />
                        </div>
                    ))}
                </div>
               
            </div>
            
            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

const DescriptionTab = ({ tmpStep, setTmpStep, save, isCreate }) => {
    const { translate } = useTranslation();
    return (
        <div className="flex flex-col mt-4">
            <p className="text-xl text-gray-600 font-bold">{translate('automatisation.description')}</p>
            <TextArea rows={5} value={tmpStep.description} onChange={(e) => setTmpStep({ ...tmpStep, description: e.target.value })} />

            {!isCreate && <div className="flex flex-row w-full flex-1 justify-end mt-4">
                <div className="flex flex-row">
                    <Button className="bg-black hover:bg-gray-800" onClick={() => save(tmpStep)}>
                        {translate('save')}
                    </Button>
                </div>
            </div>}
        </div>
    )
}

export default function MetaAudiencePanel({ setStepToClone, panelHook, step, refresh, deleteStep, isCreate, onCreateNewStep, app, appWebhookStep, userApp }) {
    const { translate } = useTranslation();
    const [isExpanded, setIsExpanded] = useState(false);
    const [tmpStep, setTmpStep] = useState({ ...step });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const api = useAPI();

    const tabs = useTabs([
        { icon: Cog8ToothIcon, name: translate('automatisation.configuration'), current: true },
        { icon: DocumentIcon, name: translate('automatisation.description'), current: false }
    ]);

    // Reset tmpStep when panel is closed
    useEffect(() => {
        if (!panelHook.open) {
            setTmpStep({ ...step });
        }
    }, [panelHook.open]);

    const save = async (tmpStep) => {


        try {

            const step = {
                ...tmpStep,
                data: {
                    ...tmpStep.data,
                    headers: tmpStep.data.headers,
                    url: tmpStep.data.url,
                    method: tmpStep.data.method,
                    body: tmpStep.data.body
                }
            }

            if (isCreate) {
                try {
                    if (!step.description || step.description.trim() === '') {
                        step.description = "Add data to Meta audience";
                    }

                    const newStepResponse = await api.createAutomatisationStep(tmpStep.automatisation_id, step);
                    if (newStepResponse?.data?.automatisation_step) {
                        await onCreateNewStep(newStepResponse.data.automatisation_step);
                    }
                } catch (e) {}
                
            } else {
                await api.updateAutomatisationStep(tmpStep.automatisation_id, step);
            }

            panelHook.setOpen(false);
            setTimeout(() => {
                refresh();
            }, 1000);
        } catch (e) {
            setError('Invalid JSON');
            return;
        }

    }

    const selectStepToClone = (step) => {
        setStepToClone(step);
        panelHook.setOpen(false);
    }


    return (
        <div className="flex flex-col flex-1 h-min rounded-md p-4 bg-white overflow-hidden">
            <div className="flex flex-row items-center">
                <img src={app?.icon} alt="webhook" className="w-12 h-12 min-w-12 min-h-12 object-fit border p-2 bg-gray-50 rounded-md mr-2 text-black" />
                <h2 className="text-xl font-bold">{app?.name}</h2>
                <div className="flex flex-row flex-1 justify-end">

                {!isCreate && <Button className="bg-red-600 hover:bg-red-300 ml-4" onClick={deleteStep}>
                        {translate('delete')}
                    </Button>}
                {isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => save(tmpStep)}>{translate('create')}</Button>}
                {!isCreate && <Button className={`${bgColor} ${hoverBgColor} ml-4 opacity-100`} onClick={() => selectStepToClone(tmpStep)}>{translate('clone')}</Button>}
                </div>
            </div>

            <div className="flex flex-row overflow-x-scroll no-scrollbar">
                <Tabs tabs={tabs} />
            </div>

            {tabs.currentTabIndex === 0 && <ConfigurationTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} userApp={userApp} appWebhookStep={appWebhookStep} />}
            {tabs.currentTabIndex === 1 && <DescriptionTab tmpStep={tmpStep} setTmpStep={setTmpStep} panelHook={panelHook} error={error} save={save} isCreate={isCreate} />}
        </div>
    )
}